<script>
    import Icon from '../icons/Icon.svelte';

    export let aiSettings = {
        activeProvider: 'gemini',
        hasGeminiKey: false,
        hasOpenaiKey: false,
        geminiKeyMasked: '',
        openaiKeyMasked: ''
    };
    export let inputGeminiKey = '';
    export let inputOpenaiKey = '';
    export let inputActiveProvider = 'gemini';
    export let isSavingAi = false;
    export let isTestingGemini = false;
    export let isTestingOpenai = false;
    export let geminiTestResult = null;
    export let openaiTestResult = null;
    export let onSaveAiSettings = () => {};
    export let onTestAi = () => {};

    let showGemini = false;
    let showOpenai = false;

    function formatMaskedKey(key) {
        if (!key) return '';
        if (key.length <= 16) return key;
        const prefix = key.slice(0, 7);
        const suffix = key.slice(-4);
        return `${prefix}...${suffix}`;
    }
</script>

<div class="tab-pane">
    <section class="master-card">
        <div class="card-header">
            <div class="card-title-group">
                <Icon name="bot" size={20} />
                <h3>Configurações Globais de IA (SaaS Master)</h3>
            </div>
            <span class="card-subtitle">Gerencie as chaves de API para transcrição de áudio, importação de cardápios e visão computacional da plataforma.</span>
        </div>

        <!-- Provedor Ativo -->
        <div class="provider-selector-box">
            <span class="selector-label">Provedor Principal Ativo para o SaaS:</span>
            <div class="provider-options">
                <label class="provider-radio {inputActiveProvider === 'gemini' ? 'active' : ''}">
                    <input type="radio" value="gemini" bind:group={inputActiveProvider} />
                    <div class="provider-info">
                        <strong>Google Gemini</strong>
                        <small>Recomendado • Alta velocidade e suporte multimodal nativo</small>
                    </div>
                </label>

                <label class="provider-radio {inputActiveProvider === 'openai' ? 'active' : ''}">
                    <input type="radio" value="openai" bind:group={inputActiveProvider} />
                    <div class="provider-info">
                        <strong>OpenAI (GPT-4o)</strong>
                        <small>Provedor alternativo com alta precisão semântica</small>
                    </div>
                </label>
            </div>
        </div>

        <div class="keys-grid">
            <!-- Google Gemini -->
            <div class="key-card {inputActiveProvider === 'gemini' ? 'is-active-provider' : ''}">
                <div class="key-card-header">
                    <div class="key-title-row">
                        <h4>Google Gemini API</h4>
                        {#if aiSettings.hasGeminiKey}
                            <span class="status-badge configured" title={aiSettings.geminiKeyMasked}>
                                ● Configurada {#if aiSettings.geminiKeyMasked}<span class="badge-key">({formatMaskedKey(aiSettings.geminiKeyMasked)})</span>{/if}
                            </span>
                        {:else}
                            <span class="status-badge missing">● Ausente</span>
                        {/if}
                    </div>
                    <span class="key-desc">Utilizada para extração visual de cardápios (PDF/Foto) e simulação do bot.</span>
                </div>

                <div class="form-group">
                    <label for="gemini-key-input">Chave de API (Google AI Studio)</label>
                    <div class="input-toggle-box">
                        {#if showGemini}
                            <input
                                id="gemini-key-input"
                                type="text"
                                bind:value={inputGeminiKey}
                                placeholder={aiSettings.hasGeminiKey ? 'Chave salva (digite para alterar)' : 'AIzaSy...'}
                                class="form-input"
                            />
                        {:else}
                            <input
                                id="gemini-key-input"
                                type="password"
                                bind:value={inputGeminiKey}
                                placeholder={aiSettings.hasGeminiKey ? 'Chave salva (digite para alterar)' : 'AIzaSy...'}
                                class="form-input"
                            />
                        {/if}
                        <button type="button" class="btn-toggle" on:click={() => showGemini = !showGemini} aria-label="Alternar visibilidade da chave Gemini">
                            <Icon name={showGemini ? 'eye-off' : 'eye'} size={14} />
                        </button>
                    </div>
                </div>

                <div class="key-actions">
                    <button
                        type="button"
                        class="btn-test-key"
                        disabled={isTestingGemini || (!inputGeminiKey && !aiSettings.hasGeminiKey)}
                        on:click={() => onTestAi('gemini', inputGeminiKey)}
                    >
                        <Icon name="zap" size={13} />
                        <span>{isTestingGemini ? 'Testando conexão...' : 'Testar Conexão Gemini'}</span>
                    </button>
                </div>

                {#if geminiTestResult}
                    <div class="test-feedback-box {geminiTestResult.success ? 'success' : 'error'}">
                        <Icon name={geminiTestResult.success ? 'check-circle' : 'alert-triangle'} size={14} />
                        <span>{geminiTestResult.message || (geminiTestResult.success ? 'Conexão validada com sucesso!' : 'Falha ao autenticar.')}</span>
                    </div>
                {/if}
            </div>

            <!-- OpenAI -->
            <div class="key-card {inputActiveProvider === 'openai' ? 'is-active-provider' : ''}">
                <div class="key-card-header">
                    <div class="key-title-row">
                        <h4>OpenAI API</h4>
                        {#if aiSettings.hasOpenaiKey}
                            <span class="status-badge configured" title={aiSettings.openaiKeyMasked}>
                                ● Configurada {#if aiSettings.openaiKeyMasked}<span class="badge-key">({formatMaskedKey(aiSettings.openaiKeyMasked)})</span>{/if}
                            </span>
                        {:else}
                            <span class="status-badge missing">● Ausente</span>
                        {/if}
                    </div>
                    <span class="key-desc">Utilizada como contingência ou provedor secundário de alta capacidade.</span>
                </div>

                <div class="form-group">
                    <label for="openai-key-input">Chave de API (OpenAI Platform)</label>
                    <div class="input-toggle-box">
                        {#if showOpenai}
                            <input
                                id="openai-key-input"
                                type="text"
                                bind:value={inputOpenaiKey}
                                placeholder={aiSettings.hasOpenaiKey ? 'Chave salva (digite para alterar)' : 'sk-proj-...'}
                                class="form-input"
                            />
                        {:else}
                            <input
                                id="openai-key-input"
                                type="password"
                                bind:value={inputOpenaiKey}
                                placeholder={aiSettings.hasOpenaiKey ? 'Chave salva (digite para alterar)' : 'sk-proj-...'}
                                class="form-input"
                            />
                        {/if}
                        <button type="button" class="btn-toggle" on:click={() => showOpenai = !showOpenai} aria-label="Alternar visibilidade da chave OpenAI">
                            <Icon name={showOpenai ? 'eye-off' : 'eye'} size={14} />
                        </button>
                    </div>
                </div>

                <div class="key-actions">
                    <button
                        type="button"
                        class="btn-test-key"
                        disabled={isTestingOpenai || (!inputOpenaiKey && !aiSettings.hasOpenaiKey)}
                        on:click={() => onTestAi('openai', inputOpenaiKey)}
                    >
                        <Icon name="zap" size={13} />
                        <span>{isTestingOpenai ? 'Testando conexão...' : 'Testar Conexão OpenAI'}</span>
                    </button>
                </div>

                {#if openaiTestResult}
                    <div class="test-feedback-box {openaiTestResult.success ? 'success' : 'error'}">
                        <Icon name={openaiTestResult.success ? 'check-circle' : 'alert-triangle'} size={14} />
                        <span>{openaiTestResult.message || (openaiTestResult.success ? 'Conexão validada com sucesso!' : 'Falha ao autenticar.')}</span>
                    </div>
                {/if}
            </div>
        </div>

        <div class="card-footer">
            <button type="button" class="btn-save-master" disabled={isSavingAi} on:click={onSaveAiSettings}>
                <Icon name="check" size={15} />
                <span>{isSavingAi ? 'Salvando...' : 'Salvar Configurações de IA'}</span>
            </button>
        </div>
    </section>
</div>

<style>
    .tab-pane {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    .master-card {
        background: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 12px;
        padding: 24px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
    }

    .card-header {
        margin-bottom: 20px;
    }

    .card-title-group {
        display: flex;
        align-items: center;
        gap: 10px;
        color: #0F172A;
    }

    .card-title-group h3 {
        font-size: 16px;
        font-weight: 700;
        margin: 0;
        color: #0F172A;
    }

    .card-subtitle {
        font-size: 13px;
        color: #64748B;
        display: block;
        margin-top: 4px;
        line-height: 1.4;
    }

    .provider-selector-box {
        background: #F8FAFC;
        border: 1px solid #E2E8F0;
        border-radius: 10px;
        padding: 16px 20px;
        margin-bottom: 20px;
    }

    .selector-label {
        font-size: 11px;
        font-weight: 700;
        color: #FF6A00;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        display: block;
        margin-bottom: 10px;
    }

    .provider-options {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
        gap: 12px;
    }

    .provider-radio {
        display: flex;
        align-items: center;
        gap: 12px;
        background: #FFFFFF;
        border: 1px solid #CBD5E1;
        border-radius: 8px;
        padding: 12px 16px;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .provider-radio:hover {
        border-color: #94A3B8;
        background: #F8FAFC;
    }

    .provider-radio.active {
        border-color: #FF6A00;
        background: #FFF7ED;
        box-shadow: 0 1px 3px rgba(255, 106, 0, 0.1);
    }

    .provider-radio input {
        accent-color: #FF6A00;
        width: 16px;
        height: 16px;
    }

    .provider-info strong {
        font-size: 14px;
        color: #0F172A;
        display: block;
    }

    .provider-info small {
        font-size: 12px;
        color: #64748B;
    }

    .keys-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
        gap: 18px;
        margin-bottom: 24px;
    }

    .key-card {
        background: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 10px;
        padding: 18px;
        display: flex;
        flex-direction: column;
        gap: 14px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
        overflow: hidden;
    }

    .key-card.is-active-provider {
        border-color: #FF6A00;
        box-shadow: 0 0 0 1px rgba(255, 106, 0, 0.25);
    }

    .key-title-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 4px;
        flex-wrap: wrap;
    }

    .key-title-row h4 {
        font-size: 14px;
        font-weight: 700;
        color: #0F172A;
        margin: 0;
        white-space: nowrap;
    }

    .key-desc {
        font-size: 12px;
        color: #64748B;
        line-height: 1.35;
        display: block;
    }

    .status-badge {
        font-size: 11px;
        font-weight: 700;
        padding: 3px 8px;
        border-radius: 4px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        max-width: 100%;
        overflow: hidden;
        white-space: nowrap;
    }

    .status-badge.configured {
        background: #DCFCE7;
        color: #166534;
        border: 1px solid #BBF7D0;
    }

    .status-badge.missing {
        background: #F1F5F9;
        color: #64748B;
        border: 1px solid #E2E8F0;
    }

    .badge-key {
        font-family: monospace;
        font-weight: 600;
        font-size: 10px;
        opacity: 0.9;
        max-width: 130px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: inline-block;
    }

    .form-group {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .form-group label {
        font-size: 12px;
        font-weight: 600;
        color: #475569;
    }

    .input-toggle-box {
        position: relative;
        display: flex;
        align-items: center;
    }

    .form-input {
        background: #FFFFFF;
        border: 1px solid #CBD5E1;
        border-radius: 8px;
        padding: 9px 38px 9px 12px;
        color: #0F172A;
        font-family: monospace;
        font-size: 13px;
        width: 100%;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
        transition: border-color 0.2s, box-shadow 0.2s;
    }

    .form-input:focus {
        outline: none;
        border-color: #FF6A00;
        box-shadow: 0 0 0 2px rgba(255, 106, 0, 0.15);
    }

    .btn-toggle {
        position: absolute;
        right: 8px;
        background: none;
        border: none;
        color: #94A3B8;
        cursor: pointer;
        padding: 4px;
        display: flex;
    }

    .btn-toggle:hover {
        color: #0F172A;
    }

    .key-actions {
        display: flex;
        align-items: center;
    }

    .btn-test-key {
        background: #F8FAFC;
        border: 1px solid #CBD5E1;
        color: #475569;
        padding: 7px 14px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        transition: all 0.15s ease;
    }

    .btn-test-key:hover:not(:disabled) {
        background: #F1F5F9;
        color: #0F172A;
        border-color: #94A3B8;
    }

    .btn-test-key:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .test-feedback-box {
        padding: 8px 12px;
        border-radius: 6px;
        font-size: 12px;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .test-feedback-box.success {
        background: #DCFCE7;
        color: #166534;
        border: 1px solid #BBF7D0;
    }

    .test-feedback-box.error {
        background: #FEE2E2;
        color: #991B1B;
        border: 1px solid #FECACA;
    }

    .card-footer {
        display: flex;
        justify-content: flex-end;
        border-top: 1px solid #E2E8F0;
        padding-top: 18px;
    }

    .btn-save-master {
        background: #FF6A00;
        border: 1px solid #EA580C;
        color: #FFFFFF;
        padding: 9px 22px;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        box-shadow: 0 1px 3px rgba(255, 106, 0, 0.2);
        transition: all 0.15s ease;
    }

    .btn-save-master:hover:not(:disabled) {
        background: #EA580C;
        box-shadow: 0 3px 8px rgba(255, 106, 0, 0.3);
    }

    .btn-save-master:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
</style>
